﻿import { RadioGroup } from "@headlessui/react";
import clsx from "clsx";
import { useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { NumberPortingFormProps } from "@/components/Checkout/Steps/NumberPorting/NumberPorting.tsx";
import { RadioButtonOption } from "@/components/Interface/FormFields/RadioButton/RadioButton.tsx";
import { Notification } from "@/components/Interface/Notification/Notification";

/**
 * Lets the user choose between keeping their number and getting a new one,
 * next to the hint that the choice cannot be made up for later.
 *
 * @param showError - Whether to display an error state (e.g., if no choice was made).
 */
export const NumberPortingChoice = ({
  showError = false,
}: {
  showError?: boolean;
}) => {
  const { t } = useTranslation();

  const { watch, setValue } = useFormContext<NumberPortingFormProps>();

  return (
    <div className="flex w-full flex-col gap-6 tablet:grid tablet:grid-cols-2 tablet:items-start">
      {/* No fieldset/legend: the RadioGroup's aria-label already names the group. */}
      <div
        className={clsx(
          "flex flex-col gap-3",
          showError && "rounded-lg border-md border-border-error p-4",
        )}
      >
        <RadioGroup
          className="flex flex-col gap-3"
          aria-label={t("Onboarding.sections.number-porting.title")}
          value={watch("enableNumberPorting")}
          onChange={(value: "yes" | "no") =>
            setValue("enableNumberPorting", value)
          }
        >
          <RadioButtonOption<NumberPortingFormProps>
            name="enableNumberPorting"
            value="yes"
            invalid={showError}
            label={
              <Trans i18nKey="Onboarding.sections.rate-selection.yesTakeNumber" />
            }
          />
          <RadioButtonOption<NumberPortingFormProps>
            name="enableNumberPorting"
            value="no"
            invalid={showError}
            label={
              <Trans i18nKey="Onboarding.sections.rate-selection.dontTakeNumber" />
            }
          />
        </RadioGroup>
      </div>

      <Notification
        dataTestid="number-porting-hint"
        title={t("Onboarding.sections.number-porting.hintTitle")}
        description={
          <Trans
            components={[<b key="not" />]}
            i18nKey="Onboarding.sections.rate-selection.numberPortingHint"
          />
        }
        tone="warning"
      />
    </div>
  );
};
